"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { Box } from "~/components/Box/Box";
import { LATEST_ORDER_COOKIE_NAME_PREFIX } from "~/constants";
import { getClientCookie } from "~/utils/cookies";
import { useButtonClasses } from "../Button/Button";
import { Text } from "../Text/Text";

interface OrderInfo {
  orderId: string;
  orderTime: string;
  trackingUrl: string;
}

// Client component to display the banner
export function LatestOrderBanner({ location }: { location: string }) {
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [cookieExists, setCookieExists] = useState(false);
  const pathname = usePathname();

  // Function to check and update cookie status
  const checkCookie = useCallback(() => {
    const latestOrderCookieName = `${LATEST_ORDER_COOKIE_NAME_PREFIX}${location}`;
    const latestOrderCookie = getClientCookie(latestOrderCookieName);
    setCookieExists(latestOrderCookie !== undefined);

    if (latestOrderCookie !== undefined) {
      try {
        const parsedOrder = JSON.parse(latestOrderCookie);

        // Format the date string to be more readable
        if (parsedOrder.orderTime) {
          const date = new Date(parsedOrder.orderTime);
          parsedOrder.orderTime = date.toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          });
        }
        setOrderInfo(parsedOrder);
      } catch (e) {
        console.error("Error parsing latestOrder cookie:", e);
        setOrderInfo(null);
      }
    } else {
      setOrderInfo(null);
    }
  }, [location]);

  useEffect(() => {
    // Initial check
    checkCookie();

    // Set up an interval to periodically check if the cookie still exists
    // This handles cases where the cookie expires
    const cookieCheckInterval = setInterval(checkCookie, 10000); // Check every 10 seconds

    // Set up a visibility change listener to check when user returns to the page
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkCookie();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Clean up
    return () => {
      clearInterval(cookieCheckInterval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkCookie]);

  const buttonClasses = useButtonClasses({ primary: true });
  const isTransactionPage = pathname.includes("/transactions/");

  if (!cookieExists || !orderInfo || isTransactionPage) {
    return null;
  }

  return (
    <Box
      background="jets-beige"
      position="relative"
      width="full"
      id="latest-order-banner"
      className="stickyHeader"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="flexEnd"
        marginX="auto"
        paddingX={["3", "5"]}
        style={{
          height: 60,
          maxWidth: "1500px",
          gap: "1.5rem",
        }}
      >
        <Text weight="regular">
          Order placed at{" "}
          <span style={{ whiteSpace: "nowrap" }}>{orderInfo.orderTime}</span>
        </Text>
        <Link href={orderInfo.trackingUrl} passHref>
          <Box
            paddingX="2"
            paddingY="1"
            className={buttonClasses}
            style={{ whiteSpace: "nowrap", padding: "0.5rem 1rem" }}
          >
            TRACK ORDER
          </Box>
        </Link>
      </Box>
    </Box>
  );
}
